import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { appQueryParams } from '../appParams'
import { lastConnectOptions } from '../appStatus'
import styles from './appStatus.module.css'
import Button from './Button'
import Screen from './Screen'
import LoadingTimer from './LoadingTimer'
import { withInjectableUi } from './extendableSystem'

// Atmospheric splash captions — rotated while loading runs in the
// background. Deliberately NOT tied to real loading progress so the
// splash reads as a cinematic intro, not a technical status bar.
const SPLASH_CAPTIONS = [
  'Initializing replay engine',
  'Calibrating timeline',
  'Synchronizing packets',
  'Preparing scene',
]
const SPLASH_CAPTION_INTERVAL_MS = 1100
const SPLASH_FINAL_CAPTION = 'Ready to launch'

const AppStatusBase = ({
  status,
  isError,
  isOpen = true,
  hideDots = false,
  lastStatus = '',
  backAction = undefined as undefined | (() => void),
  description = undefined as undefined | string | JSX.Element,
  actionsSlot = null as React.ReactNode | null,
  showReconnect = false,
  onReconnect = undefined as undefined | (() => void),
  children
}) => {
  const lockConnect = appQueryParams.lockConnect === 'true'
  const connectOptions = lastConnectOptions.value

  // Rotating atmospheric caption. Cycles through SPLASH_CAPTIONS every
  // SPLASH_CAPTION_INTERVAL_MS, then settles on the final caption. The
  // real status from progressReporter is NOT shown — loading happens in
  // the background while the splash plays its cinematic sequence.
  const [captionIndex, setCaptionIndex] = useState(0)
  const captionTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  useEffect(() => {
    if (isError) return
    captionTimerRef.current = setInterval(() => {
      setCaptionIndex(prev => {
        if (prev >= SPLASH_CAPTIONS.length) return prev
        return prev + 1
      })
    }, SPLASH_CAPTION_INTERVAL_MS)
    return () => {
      if (captionTimerRef.current) clearInterval(captionTimerRef.current)
    }
  }, [isError])

  const splashCaption = isError
    ? status
    : captionIndex >= SPLASH_CAPTIONS.length
      ? SPLASH_FINAL_CAPTION
      : SPLASH_CAPTIONS[Math.min(captionIndex, SPLASH_CAPTIONS.length - 1)]

  return (
    <div className=''>
      <Screen
        className={`small-content ${styles.appStatusScreen} app-status-screen`}
        titleSelectable={isError}
        title={
          <>
            {/* Portaled straight to #ui-root, NOT rendered inline here: nested
               inside .screen-content it inherits that card's own fixed-position
               containing block (its overflow: auto plus the entrance transform
               animation), so `position: fixed; inset: 0` would center against
               the small card instead of the viewport and get clipped by
               .fullscreen's overflow: hidden. As a direct #ui-root child it
               shares #ui-root's own (permanent, viewport-filling) transform,
               same as .fullscreen itself. */}
            {!isError && isOpen && createPortal(
              <>
                {/* Neon shadow stains on the background — soft coloured glows
                   (blue + gold, the staff-line palette) drifting across the
                   right-docked splash area. */}
                <div className={styles.splashNeonBlue} aria-hidden="true" />
                <div className={styles.splashNeonGold} aria-hidden="true" />
                {/* Portalled glow — sits directly in #ui-root so it's never
                   clipped. Radial gradient centered behind the caption area. */}
                <div className={styles.splashGlow} aria-hidden="true" />
                {/* Metallic stripes — 4 bars on top, all same height,
                   spanning 75% of the screen from the right edge. */}
                <div className={styles.stripeBlock} aria-hidden="true">
                  <i /><i /><i /><i />
                </div>
                {/* Diamond + caption — under the stripes, right-anchored. */}
                <div className={styles.statusDock}>
                  <div className={styles.statusRow}>
                    <span className={styles.statusDiamondFixed} aria-hidden="true">
                      <span className={styles.statusDiamondFixedInner} />
                    </span>
                    <span
                      className={styles.appStatusTitle}
                      data-text={typeof splashCaption === 'string' ? splashCaption : undefined}
                    >
                      {splashCaption}
                    </span>
                  </div>
                </div>
              </>,
              document.querySelector('#ui-root')!
            )}
            {isError && (
              <div className={styles.statusLine}>
                <span
                  className={styles.appStatusTitleError}
                  data-text={typeof status === 'string' ? status : undefined}
                >
                  {status}
                </span>
              </div>
            )}
            {description && <p className={styles.description}>{description}</p>}
            {!!lastStatus && <p className={styles['last-status']}>{`Last status: ${lastStatus}`}</p>}
            {isError && <p className={`app-status-title-context-info ${styles.appStatusTitleContextInfo}`}>
              S: {connectOptions?.server ?? 'N/A'} {' '}
              V: {connectOptions?.botVersion ?? 'auto'} {' '}
              U: {connectOptions?.username ?? 'N/A'}{' '}
              cV: {process.env.RELEASE_TAG ?? 'N/A'}
            </p>}
          </>
        }
        backdrop='dirt'
      >
        {isError && (
          <>
            {showReconnect && onReconnect && <Button onClick={onReconnect}>
              <b>Reconnect</b>
            </Button>}
            {actionsSlot}
            {!lockConnect && <Button
              onClick={() => {
                if (location.search) {
                  location.search = ''
                } else {
                  window.location.reload()
                }
              }}
            >
              <b>Reset App (recommended)</b>
            </Button>}
            {backAction && <Button label="Back" onClick={backAction} />}
          </>
        )}
        {children}
        <LoadingTimer />
      </Screen>
    </div>
  )
}

export default withInjectableUi(AppStatusBase, 'appStatus')
